import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Tabs,
  Tab,
  Button,
  useMediaQuery,
  Input,
  Typography,
  IconButton,
  CircularProgress,
  Avatar,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import Picker from "@emoji-mart/react";
import data from "@emoji-mart/data";

import { useSearchPanelStyles } from "./style";
import { useLocation } from "react-router-dom";
import ArrowsSort from "../../../../assets/ArrowsSort";
import { useAppSelector } from "../../../../redux/typedHooks";
import defaultAvatars from "../../../../assets/defaultAvatars.svg";
import { getFileByIdAPI } from "../../../../api/userApi";
import { showToast } from "../../../../utils/toast";
import { getUserType } from "../../../../utils/getUserType";
import FileIconUpload from "../../../../assets/images/icons/fileIconUpload.svg";
import SendIconButton from "../../../../assets/images/icons/sendIconButton.svg";
import UploadMediaPostModal from "./UploadMediaPostModal";
import SmilleIcon from "../../../../assets/svgs/smilleIcon.svg";
import PlusIcon from "../../../../assets/svgs/plusIcon copy.svg";
import { useCreatePublicContentMutation } from "../../../../query/hooks/useCreatePublicContentMutation";

const SearchPanel = ({
  visionType,
  setVisionType,
  showSuggestions,
  toggleSuggestions,
  onToggleSuggestions,
  onPostCreated,
}: {
  visionType: string;
  setVisionType: (visionType: string) => void;
  showSuggestions: boolean;
  toggleSuggestions: () => void;
  onToggleSuggestions: () => void;
  onPostCreated?: () => void;
}) => {
  const classes = useSearchPanelStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();
  const history = useHistory();
  const user = useAppSelector((state: any) => state.user.currentUser);

  const searchParams = new URLSearchParams(location.search);
  const initialSortBy = (() => {
    // Спочатку перевіряємо URL параметри
    const urlSortBy = searchParams.get("sortBy");
    if (urlSortBy === "recent" || urlSortBy === "popular") {
      return urlSortBy as "popular" | "recent";
    }
    // Потім перевіряємо localStorage
    const savedSortBy = localStorage.getItem("sortBy");
    if (savedSortBy === "recent" || savedSortBy === "popular") {
      return savedSortBy as "popular" | "recent";
    }
    // За замовчуванням
    return "popular";
  })();
  const [sortBy, setSortBy] = useState<"popular" | "recent">(initialSortBy);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [value, setValue] = useState(() => (visionType === "Explore" ? 1 : 0));
  const [userAvatar, setUserAvatar] = useState<string>(defaultAvatars);
  const [description, setDescription] = useState<string>("");
  const [price, setPrice] = useState<string>("");
  const [isPostLoading, setIsPostLoading] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);
  const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [agreementAccepted] = useState<boolean>(true);
  const [isPaidContent, setIsPaidContent] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const createPublicContentMutation = useCreatePublicContentMutation();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(target) &&
        emojiButtonRef.current &&
        !emojiButtonRef.current.contains(target)
      ) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside);
      }, 100);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showEmojiPicker]);

  useEffect(() => {
    if (visionType === "Explore") {
      setValue(1);
    } else {
      setValue(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visionType, setVisionType]);

  useEffect(() => {
    const fetchUserAvatar = async () => {
      if (!user?.profileImg || user.profileImg.trim() === "") {
        setUserAvatar("");
        return;
      }

      // Перевіряємо, чи це не дефолтний або невалідний шлях
      if (
        user.profileImg.includes("avatarViewer.png") ||
        user.profileImg.includes("defaultImage") ||
        user.profileImg.includes("defaultAvatars")
      ) {
        setUserAvatar("");
        return;
      }

      // Якщо це Google URL (lh3.googleusercontent.com), використовуємо його напряму
      if (user.profileImg.includes("lh3.googleusercontent.com")) {
        setUserAvatar(user.profileImg);
        return;
      }
      if (user.profileImg.startsWith("/api/files/")) {
        try {
          const fileId = user.profileImg.split("/api/files/")[1];
          if (!fileId || fileId.trim() === "") {
            setUserAvatar("");
            return;
          }
          const blobUrl = await getFileByIdAPI(fileId);
          setUserAvatar(blobUrl);
        } catch (err) {
          console.error(`Error fetching avatar for ${user.profileImg}:`, err);
          setUserAvatar("");
        }
      } else {
        // Перевіряємо, чи це валідний URL
        if (
          user.profileImg.startsWith("http://") ||
          user.profileImg.startsWith("https://") ||
          user.profileImg.startsWith("data:")
        ) {
          setUserAvatar(user.profileImg);
        } else {
          setUserAvatar("");
        }
      }
    };

    fetchUserAvatar();
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isFilterOpen &&
        dropdownRef.current &&
        filterButtonRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !filterButtonRef.current.contains(event.target as Node)
      ) {
        setIsFilterOpen(false);
      }
    };

    if (isFilterOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isFilterOpen]);

  const updateSortBy = (value: "popular" | "recent") => {
    setSortBy(value);
    setIsFilterOpen(false);

    // Зберігаємо вибір в localStorage
    localStorage.setItem("sortBy", value);

    const params = new URLSearchParams(location.search);
    params.set("sortBy", value);
    history.push(`?${params.toString()}`);
  };

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    const visionType = newValue === 0 ? "My Subscription" : "Explore";
    setValue(newValue);
    setVisionType(visionType);
  };

  const handleInputContainerClick = () => {
    inputRef.current?.focus();
  };

  const handleMediaButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsMediaModalOpen(true);
  };

  const handleReopenAttachedMedia = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (attachedFiles.length === 0) return;
    setIsMediaModalOpen(true);
  };

  const handleCloseMediaModal = () => {
    setIsMediaModalOpen(false);
  };

  const handleMediaConfirm = (
    files: File[],
    meta?: { isPaidContent: boolean; price: string }
  ) => {
    setAttachedFiles(files);
    if (meta) {
      setIsPaidContent(meta.isPaidContent);
      setPrice(meta.price);
    }
    setIsMediaModalOpen(false);
  };

  const handleSendClick = async () => {
    // Get description from state
    const trimmedDescription = description.trim();

    // Validate description
    if (!trimmedDescription) {
      showToast("Please enter a description", "error");
      return;
    }

    // Validate files
    if (attachedFiles.length === 0) {
      showToast("Please attach at least one file", "error");
      return;
    }

    setIsPostLoading(true);
    try {
      // Parse price if provided
      const priceValue = price.trim() ? parseFloat(price.trim()) : 0;
      const hasPrice = priceValue > 0;

      // Validate minimum price for private posts
      if (hasPrice && priceValue < 5) {
        showToast("Minimum price for private posts is $5.00", "error");
        setIsPostLoading(false);
        return;
      }

      // Determine post type based on files and price
      const hasVideo = attachedFiles.some(file =>
        file.type.startsWith("video/")
      );
      const hasImage = attachedFiles.some(file =>
        file.type.startsWith("image/")
      );
      const postType =
        hasVideo && hasImage
          ? "Bundle"
          : hasVideo
            ? hasPrice
              ? "Fixed price video"
              : "Free video"
            : hasPrice
              ? "Fixed price image"
              : "Free image";

      await createPublicContentMutation.mutateAsync({
        description: trimmedDescription,
        hashtags: [],
        type: postType,
        price: priceValue,
        files: attachedFiles,
        contentProviderAgreementAccepted: true,
      });

      showToast("Your post has been published!", "success");

      // Clear attached files after send
      setAttachedFiles([]);
      // Clear input
      setDescription("");
      // Clear price
      setPrice("");
      setIsPaidContent(false);

      // Refresh feed after post creation
      if (onPostCreated) {
        onPostCreated();
      }
    } catch (error: any) {
      console.error("Error creating post:", error);
      showToast(error.message || "Error while creating post!", "error");
    } finally {
      setIsPostLoading(false);
    }
  };

  const handleEmojiSelect = (emoji: any) => {
    const emojiText = emoji?.native || emoji?.unified || "";
    const currentValue = description;
    const start = cursorPosition;
    const end = cursorPosition;

    const newValue =
      currentValue.substring(0, start) +
      emojiText +
      currentValue.substring(end);
    setDescription(newValue);

    setTimeout(() => {
      if (inputRef.current) {
        const input = inputRef.current;
        const newCursorPosition = start + emojiText.length;
        input.focus();
        setTimeout(() => {
          input.setSelectionRange(newCursorPosition, newCursorPosition);
          setCursorPosition(newCursorPosition);
        }, 0);
      }
    }, 50);

    setShowEmojiPicker(false);
  };

  const userType = getUserType(user);
  const userTypeTitle = userType?.title?.toLowerCase();
  const isCreator = userTypeTitle === "model" || userTypeTitle === "creator";

  const handleAvatarClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (user?.username) {
      history.push(`/${user.username}`);
    } else if (user?._id) {
      history.push(`/${user._id}`);
    }
  };

  return (
    <div>
      {/* New post creation block - only for creators */}
      {isCreator && (
        <Box
          onClick={handleInputContainerClick}
          className={classes.postCreationBlock}
        >
          <Box
            onClick={handleInputContainerClick}
            className={classes.postCreationContent}
          >
            {userAvatar &&
              userAvatar !== defaultAvatars &&
              userAvatar !== "" ? (
              <img
                src={userAvatar}
                alt="User avatar"
                onClick={handleAvatarClick}
                className={classes.userAvatar}
              />
            ) : (
              <Avatar
                className={classes.userAvatar}
                onClick={handleAvatarClick}
              >
                {user?.firstName?.[0] && user?.lastName?.[0]
                  ? `${user.firstName[0]}${user.lastName[0]}`
                  : user?.firstName?.[0] || user?.username?.[0] || "U"}
              </Avatar>
            )}
            <Box className={classes.postInputWrapper}>
              <Box
                className={classes.postInputContainer}
                onClick={handleInputContainerClick}
                style={{ cursor: "text" }}
              >
                <Input
                  ref={inputRef}
                  placeholder="What's on your mind?"
                  className={classes.postInput}
                  disableUnderline
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  onClick={(e: React.MouseEvent<HTMLInputElement>) => {
                    const target = e.target as HTMLInputElement;
                    setCursorPosition(target.selectionStart || 0);
                  }}
                  onSelect={(e: React.SyntheticEvent<HTMLInputElement>) => {
                    const target = e.target as HTMLInputElement;
                    setCursorPosition(target.selectionStart || 0);
                  }}
                  onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    const target = e.target as HTMLInputElement;
                    setCursorPosition(target.selectionStart || 0);
                  }}
                />
              </Box>
            </Box>
            {attachedFiles.length === 0 && (
              <Box className={classes.postActions}>
                <IconButton
                  ref={emojiButtonRef}
                  onClick={e => {
                    e.stopPropagation();
                    setShowEmojiPicker(prev => !prev);
                  }}
                  className={classes.smilleButton}
                >
                  <img
                    src={SmilleIcon}
                    alt="Emoji"
                    className={classes.smilleIcon}
                  />
                </IconButton>
                <Button
                  className={classes.imageUploadButton}
                  onClick={handleMediaButtonClick}
                >
                  <img src={PlusIcon} alt="Add media" />
                </Button>
                {showEmojiPicker && (
                  <div ref={emojiPickerRef} className={classes.boxSmile}>
                    <Picker
                      data={data}
                      onEmojiSelect={handleEmojiSelect}
                      theme="dark"
                    />
                  </div>
                )}
              </Box>
            )}
            {attachedFiles.length > 0 && (
              <Box
                className={classes.attachedFilesContainer}
                onClick={handleReopenAttachedMedia}
                style={{ cursor: "pointer" }}
              >
                <Box className={classes.attachedFilesWrapper}>
                  <Box className={classes.uploadIconWrapper}>
                    <img
                      src={FileIconUpload}
                      alt="File icon"
                      style={{ width: "20px", height: "20px" }}
                    />
                  </Box>
                  {!isMobile && (
                    <Typography className={classes.attachedFilesText}>
                      {attachedFiles.length} file
                      {attachedFiles.length > 1 ? "s" : ""} attached
                    </Typography>
                  )}
                  {isMobile && (
                    <Typography className={classes.mobileAttachedFilesText}>
                      {attachedFiles.length}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
            {attachedFiles.length > 0 && (
              <Button
                className={classes.sendButton}
                onClick={handleSendClick}
                disabled={isPostLoading}
              >
                {isPostLoading ? (
                  <CircularProgress size={20} style={{ color: "#FFFFFF" }} />
                ) : (
                  <img
                    src={SendIconButton}
                    alt="Send"
                    style={{ width: "24px", height: "24px" }}
                  />
                )}
              </Button>
            )}
          </Box>
        </Box>
      )}

      <div className={classes.searchPanel}>
        <Box
          className={classes.searchPanelContent}
          width="100%"
          display="flex"
          alignItems="center"
        >
          <Box className={classes.searchPanelContentHelp} flexShrink={0}>
            <Box
              className={classes.tabWrapper}
              style={{ display: "flex", alignItems: "center", gap: "12px" }}
            >
              <Tabs
                value={value}
                onChange={handleTabChange}
                aria-label="feed tabs"
                TabIndicatorProps={{ style: { display: "none" } }}
                style={
                  isMobile
                    ? {
                      maxHeight: 38,
                      height: 38,
                    }
                    : undefined
                }
              >
                <Tab
                  label="My Subscriptions"
                  className={`${classes.tab} ${value === 0 ? classes.activeTab : ""
                    }`}
                  style={
                    isMobile
                      ? {
                        fontSize: 14,
                        maxHeight: 34,
                      }
                      : undefined
                  }
                />
                <Tab
                  label="Explore"
                  className={`${classes.tab} ${value === 1 ? classes.activeTab : ""
                    }`}
                  style={
                    isMobile
                      ? {
                        fontSize: 14,
                        maxHeight: 34,
                      }
                      : undefined
                  }
                />
              </Tabs>
            </Box>
          </Box>
          <Box flexGrow={1} className={classes.searchHelp} />

          <Box
            display="flex"
            alignItems="center"
            flexShrink={0}
            style={{
              gap: isMobile ? "8px" : "12px",
              marginLeft: "auto",
              justifyContent: "flex-end",
              width: isMobile ? "auto" : "unset",
            }}
          >
            {/* <button
              style={{
                backgroundColor: "transparent",
                border: "none",
                cursor: "pointer",
                padding: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onClick={toggleSuggestions}
              title={showSuggestions ? "Hide Suggestions" : "Show Suggestions"}
            >
              {showSuggestions ? (
                <Eye color="#FFFFFF" size={isMobile ? 20 : 22} />
              ) : (
                <EyeOff color="#FFFFFF" size={isMobile ? 20 : 22} />
              )}
            </button> */}

            {!isMobile && (
              <Button
                ref={filterButtonRef}
                onClick={() => setIsFilterOpen(prev => !prev)}
                className={classes.filterButton}
                disableRipple
                style={
                  isMobile
                    ? {
                      fontSize: 14,
                      padding: "4px 10px",
                      minWidth: "auto",
                      borderRadius: 8,
                      marginLeft: 0,
                    }
                    : undefined
                }
              >
                <div
                  style={{
                    marginRight: "8px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <ArrowsSort />
                </div>
                <div style={{ marginRight: "4px", fontSize: 20 }}>
                  <b>
                    {sortBy === "popular"
                      ? "Popular"
                      : sortBy === "recent"
                        ? "Newest"
                        : "Filter"}
                  </b>{" "}
                </div>
                <span style={{ fontSize: 20 }}>first</span>
              </Button>
            )}

            {isFilterOpen && (
              <div
                ref={dropdownRef}
                className={classes.dropdown}
                style={
                  isMobile
                    ? {
                      right: 0,
                      top: 44,
                      width: 110,
                    }
                    : undefined
                }
              >
                <div
                  className={classes.option}
                  style={{ fontWeight: 400 }}
                  onClick={() => updateSortBy("popular")}
                >
                  Popular
                </div>
                <div
                  className={classes.option}
                  style={{ fontWeight: 400 }}
                  onClick={() => updateSortBy("recent")}
                >
                  Newest
                </div>
              </div>
            )}
          </Box>
        </Box>
      </div>

      {/* Media Upload Modal - same as home create post */}
      <UploadMediaPostModal
        open={isMediaModalOpen}
        onClose={handleCloseMediaModal}
        onConfirm={handleMediaConfirm}
        showPaidContent={true}
        showAgreement={true}
        agreementAccepted={agreementAccepted}
        maxFiles={10}
        initialFiles={attachedFiles}
        initialMeta={{ isPaidContent, price }}
      />
    </div>
  );
};

export default SearchPanel;
